import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9 } from "styled-components";
import React from "react";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { Grid } from "~/features/ui/grid";
import { Slider } from "~/features/ui/slider";
import { Progress } from "~/features/ui/slider/progress";
import { takeFirst } from "~/features/app/utils";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type ProductRowBlockViewProps<TProduct = unknown> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  getProductKey?: (product: TProduct, index: number) => React.Key;
  hasTopDivider?: boolean | null;
  mode?: string | null;
  products?: TProduct[] | null;
  renderProduct: (product: TProduct, index: number, mode: Mode) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  theme?: string | null;
};
export function ProductRowBlockView<TProduct = unknown>({
  anchor,
  getProductKey,
  hasTopDivider = true,
  mode = "light",
  products = [],
  renderProduct,
  sectionProps,
  theme = "white"
}: ProductRowBlockViewProps<TProduct>) {
  const [activeSlideIndex, setActiveSlideIndex] = React.useState(0);
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  const items = products ?? [];
  // v3: white theme renders true white (helper maps "white" to grey-20).
  const surface = theme === "grey" ? themeStyle.background : {
    "backgroundColor": "var(--bg-primary)"
  };
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
    "borderTopWidth": "1px"
  }, themeStyle.text, themeStyle.border, surface]}>
      <Wrap mode={mode as Mode}>
        <_StyledDiv>
          <_StyledDiv2>
            <_StyledGrid $_css2={[{
            "position": "relative",
            "zIndex": "10",
            "gap": "0.1rem",
            "borderLeftWidth": "1px",
            "borderRightWidth": "1px"
          }, themeStyle.border, surface]}>
              {items.map((product, idx) => <_StyledDiv3 key={getProductKey?.(product, idx) || idx} $_css3={[{
              "gridColumn": "1 / -1",
              "padding": "4rem",
              "outlineStyle": "solid",
              "outlineWidth": "1px",
              "@media (min-width: 1024px)": {
                "gridColumn": "span 3 / span 3"
              },
              "@media (min-width: 1536px)": {
                "gridColumn": "span 3 / span 3"
              }
            }, themeStyle.outline]}>
                  {renderProduct(product, idx, mode as Mode)}
                </_StyledDiv3>)}
            </_StyledGrid>
          </_StyledDiv2>
          <_StyledDiv4>
            <_StyledDiv5 $_css4={[{
            "position": "relative",
            "zIndex": "10",
            "borderBottomWidth": "1px"
          }, themeStyle.border, surface]}>
              <Slider options={{
              fixedWidth: "60%"
            }} hasTrack onMove={splide => setActiveSlideIndex(splide.Components.Controller.getIndex())}>
                {items.map((product, idx) => <Slider.Slide key={getProductKey?.(product, idx) || idx}>
                    <_StyledDiv6 $_css5={[{
                  "height": "100%",
                  "borderRightWidth": "1px",
                  "padding": "1.6rem"
                }, idx === 0 && {
                  "borderLeftWidth": "1px"
                }, themeStyle.border]}>
                      {renderProduct(product, idx, mode as Mode)}
                    </_StyledDiv6>
                  </Slider.Slide>)}
              </Slider>
            </_StyledDiv5>
            <_StyledDiv7 $_css6={[{
            "position": "relative",
            "zIndex": "10",
            "paddingLeft": "2.4rem",
            "paddingRight": "2.4rem",
            "paddingTop": "3.2rem",
            "paddingBottom": "3.2rem"
          }, surface]}>
              <Progress activeSlideIndex={activeSlideIndex} totalSlides={items.length} />
            </_StyledDiv7>
          </_StyledDiv4>
        </_StyledDiv>
      </Wrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-1tiif6l-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-1tiif6l-1"
})({
  "@media (min-width: 1024px)": {
    "paddingLeft": "4rem",
    "paddingRight": "4rem"
  }
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-1tiif6l-2"
})({
  "display": "none",
  "@media (min-width: 1024px)": {
    "display": "block"
  }
});
var _StyledGrid = _styled4(Grid).withConfig({
  componentId: "sc-sf-1tiif6l-3"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled5("div").withConfig({
  componentId: "sc-sf-1tiif6l-4"
})`${p => p.$_css3}`;
var _StyledDiv4 = _styled6("div").withConfig({
  componentId: "sc-sf-1tiif6l-5"
})({
  "display": "block",
  "@media (min-width: 1024px)": {
    "display": "none"
  }
});
var _StyledDiv5 = _styled7("div").withConfig({
  componentId: "sc-sf-1tiif6l-6"
})`${p => p.$_css4}`;
var _StyledDiv6 = _styled8("div").withConfig({
  componentId: "sc-sf-1tiif6l-7"
})`${p => p.$_css5}`;
var _StyledDiv7 = /*#__PURE__*/_styled9("div").withConfig({
  displayName: "product-row___StyledDiv7",
  componentId: "sc-1ka2xoa-0"
})(["", ""], p => p.$_css6);